import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Heart, Users, BookOpen, ZoomIn } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import ImageLightbox from '../components/ImageLightbox';
import { useSiteSettings } from '../context/SiteSettingsContext';

// Helper to parse date string without timezone shift
const parseLocalDate = (dateStr) => {
  if (!dateStr) return new Date();
  if (dateStr.length === 10 && dateStr.includes('-')) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(dateStr);
};

// Helper to format time to 12-hour AM/PM format
const formatTime12Hour = (timeStr) => {
  if (!timeStr) return '';
  if (timeStr.toLowerCase().includes('am') || timeStr.toLowerCase().includes('pm')) {
    return timeStr;
  }
  const [hours, minutes] = timeStr.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

function Home() {
  const { settings, getFullAddress } = useSiteSettings();
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState({ url: '', alt: '' });

  const openLightbox = (imageUrl, imageAlt) => {
    setLightboxImage({ url: imageUrl, alt: imageAlt });
    setLightboxOpen(true);
  };

  const fetchData = useCallback(async () => {
    try {
      // Fetch both in parallel
      const [eventsRes, announcementsRes] = await Promise.all([
        axios.get(`${API}/events/featured`),
        axios.get(`${API}/announcements`)
      ]);
      setFeaturedEvents(eventsRes.data.slice(0, 3));
      setAnnouncements(announcementsRes.data.slice(0, 3));
    } catch (error) {
      console.error('Failed to load home page data');
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div>
      {/* Hero Section */}
      <div 
        className="hero-section" 
        style={{
          backgroundImage: `url(${settings.hero_image_url || 'https://images.unsplash.com/photo-1653133672754-82025e7e9074?crop=entropy&cs=srgb&fm=jpg&q=85'})`,
          minHeight: '600px'
        }}
      >
        <div className="hero-overlay min-h-[600px] flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center text-white">
            <div className="inline-block bg-white bg-opacity-95 rounded-3xl p-10 mb-8 shadow-2xl">
              <img 
                src={settings.logo_url || "https://customer-assets.emergentagent.com/job_coosbaychurch/artifacts/zfdqhplq_Coosbay%20logo%20plain.jpg"}
                alt={`${settings.church_name} Logo`}
                className="h-48 md:h-64 w-auto mx-auto"
              />
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="hero-title">
              {settings.hero_title || `Welcome to ${settings.church_name}`}
            </h1>
            <p className="text-2xl md:text-3xl mb-8 font-medium">{settings.hero_subtitle || 'Loving God and Others'}</p>
            <p className="text-xl md:text-2xl mb-12 max-w-3xl mx-auto">{settings.hero_tagline || 'Biblical Teaching • Traditional Worship • Community'}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/events" className="btn-primary" data-testid="plan-visit-btn">Plan Your Visit</Link>
              <Link to="/giving" className="btn-secondary" data-testid="give-now-btn">Give Now</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Service Times */}
      {settings.show_service_times !== false && (
      <div className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }} data-testid="service-times-title">
            {settings.service_section_title || 'Join Us This Sunday'}
          </h2>
          <div className={`grid gap-8 ${settings.show_service_3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
            {settings.show_sunday_school !== false && (
            <div className="card p-8 text-center" data-testid="sunday-school-card">
              <Calendar size={48} color="#f59e0b" className="mx-auto mb-4" />
              <h3 className="text-3xl font-semibold mb-4" style={{ color: '#1e3a8a' }}>{settings.sunday_school_label || 'Sunday School'}</h3>
              <p className="text-2xl font-bold text-amber-500 mb-2">{settings.sunday_school_time}</p>
              {settings.sunday_school_location && (
                <p className="text-lg text-slate-700 font-medium mb-1">{settings.sunday_school_location}</p>
              )}
              {settings.sunday_school_description && (
                <p className="text-base text-slate-500">{settings.sunday_school_description}</p>
              )}
              {!settings.sunday_school_location && (
                <p className="text-lg text-slate-600">{getFullAddress()}</p>
              )}
            </div>
            )}
            {settings.show_sunday_service !== false && (
            <div className="card p-8 text-center" data-testid="sunday-service-card">
              <BookOpen size={48} color="#f59e0b" className="mx-auto mb-4" />
              <h3 className="text-3xl font-semibold mb-4" style={{ color: '#1e3a8a' }}>{settings.sunday_service_label || 'Sunday Service'}</h3>
              <p className="text-2xl font-bold text-amber-500 mb-2">{settings.sunday_service_time}</p>
              {settings.sunday_service_location && (
                <p className="text-lg text-slate-700 font-medium mb-1">{settings.sunday_service_location}</p>
              )}
              {settings.sunday_service_description && (
                <p className="text-base text-slate-500">{settings.sunday_service_description}</p>
              )}
              {!settings.sunday_service_location && (
                <p className="text-lg text-slate-600">{getFullAddress()}</p>
              )}
            </div>
            )}
            {settings.show_service_3 === true && (
            <div className="card p-8 text-center" data-testid="service-3-card">
              <Users size={48} color="#f59e0b" className="mx-auto mb-4" />
              <h3 className="text-3xl font-semibold mb-4" style={{ color: '#1e3a8a' }}>{settings.service_3_label || 'Bible Study'}</h3>
              <p className="text-2xl font-bold text-amber-500 mb-2">{settings.service_3_time}</p>
              {settings.service_3_location && (
                <p className="text-lg text-slate-700 font-medium mb-1">{settings.service_3_location}</p>
              )}
              {settings.service_3_description && (
                <p className="text-base text-slate-500">{settings.service_3_description}</p>
              )}
              {!settings.service_3_location && (
                <p className="text-lg text-slate-600">{getFullAddress()}</p>
              )}
            </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Mission Statement */}
      {settings.show_mission !== false && (
      <div className="section-padding" style={{ backgroundColor: '#fffbeb' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-8" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }} data-testid="mission-title">
            {settings.mission_section_title || 'Our Mission'}
          </h2>
          <p className="text-2xl md:text-3xl italic mb-6" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }}>
            &ldquo;{settings.mission_statement}&rdquo;
          </p>
          <p className="text-xl text-slate-600 mb-4">{settings.mission_verse || 'Philippians 2:5'}</p>
          <p className="text-xl text-slate-700 leading-relaxed">
            {settings.mission_description || `We are a faith community dedicated to loving God and others through Biblical teaching and traditional worship. Everyone is welcome at ${settings.church_name}.`}
          </p>
        </div>
      </div>
      )}

      {/* Featured Events */}
      {featuredEvents.length > 0 && (
        <div className="section-padding bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-12" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }}>
              Featured Events
            </h2>
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              {featuredEvents.map((event) => (
                <div key={event.id} className="card p-6">
                  {event.image_url ? (
                    <div 
                      className="relative group cursor-pointer mb-4 overflow-hidden rounded-lg bg-slate-100"
                      onClick={() => openLightbox(event.image_url, event.title)}
                      data-testid={`featured-event-image-${event.id}`}
                    >
                      <div className="aspect-[4/3] w-full">
                        <img src={event.image_url} alt={event.title} className="w-full h-full object-contain bg-white transition-transform duration-300 group-hover:scale-105" />
                      </div>
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center rounded-lg">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white rounded-full p-3 shadow-lg">
                          <ZoomIn size={24} className="text-blue-900" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="aspect-[4/3] w-full rounded-lg mb-4 bg-gradient-to-br from-blue-100 to-amber-100 flex items-center justify-center">
                      <Calendar size={64} className="text-blue-300" />
                    </div>
                  )}
                  <h3 className="text-2xl font-bold mb-3" style={{ color: '#1e3a8a' }}>{event.title}</h3>
                  <p className="text-lg text-slate-600 mb-2">
                    {parseLocalDate(event.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </p>
                  <p className="text-lg text-slate-600 mb-4">{formatTime12Hour(event.time)}</p>
                  <Link to="/events" className="btn-secondary block text-center">View Details</Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Latest Announcements */}
      {announcements.length > 0 && (
        <div className="section-padding" style={{ backgroundColor: '#fffbeb' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-block bg-white rounded-2xl shadow-lg p-6 mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_1f958f9a-6dc9-4c4c-9c01-3c2b02fb839c/artifacts/utqs21ui_4.jpg" 
                  alt="Coos Bay Cornerstone Logo" 
                  className="h-32 w-auto mx-auto"
                />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }}>
                Latest Announcements
              </h2>
              <div className="flex items-center justify-center mt-4 gap-3">
                <div className="h-1 w-24 bg-amber-500 rounded-full"></div>
                <div className="h-2 w-2 bg-blue-900 rounded-full"></div>
                <div className="h-1 w-24 bg-amber-500 rounded-full"></div>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {announcements.map((announcement) => (
                <div key={announcement.id} className="card p-6">
                  {announcement.image_url && (
                    <div 
                      className="relative group cursor-pointer mb-4 overflow-hidden rounded-lg bg-slate-100"
                      onClick={() => openLightbox(announcement.image_url, announcement.title)}
                      data-testid={`announcement-image-${announcement.id}`}
                    >
                      <div className="aspect-[4/3] w-full">
                        <img src={announcement.image_url} alt={announcement.title} className="w-full h-full object-contain bg-white transition-transform duration-300 group-hover:scale-105" />
                      </div>
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center rounded-lg">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white rounded-full p-3 shadow-lg">
                          <ZoomIn size={24} className="text-blue-900" />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="mb-3">
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                      announcement.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                      announcement.priority === 'high' ? 'bg-amber-100 text-amber-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {announcement.priority.toUpperCase()}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold mb-3" style={{ color: '#1e3a8a' }}>{announcement.title}</h3>
                  <p className="text-lg text-slate-600">{announcement.content}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quick Links */}
      {settings.show_quick_links !== false && (
      <div className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="card p-8 text-center" data-testid="upcoming-events-card">
              <Calendar size={56} color="#1e3a8a" className="mx-auto mb-6" />
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>{settings.quicklink1_title || 'Upcoming Events'}</h3>
              <p className="text-lg text-slate-600 mb-6">{settings.quicklink1_description || 'Join us for special events and weekly activities'}</p>
              <Link to="/events" className="btn-primary" data-testid="view-events-btn">{settings.quicklink1_button || 'View Events'}</Link>
            </div>
            <div className="card p-8 text-center" data-testid="get-involved-card">
              <Users size={56} color="#1e3a8a" className="mx-auto mb-6" />
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>{settings.quicklink2_title || 'Get Involved'}</h3>
              <p className="text-lg text-slate-600 mb-6">{settings.quicklink2_description || 'Connect with our community through various ministries'}</p>
              <Link to="/about" className="btn-primary" data-testid="learn-more-btn">{settings.quicklink2_button || 'Learn More'}</Link>
            </div>
            <div className="card p-8 text-center" data-testid="support-church-card">
              <Heart size={56} color="#1e3a8a" className="mx-auto mb-6" />
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>{settings.quicklink3_title || 'Support Our Church'}</h3>
              <p className="text-lg text-slate-600 mb-6">{settings.quicklink3_description || 'Help those in need through your generous giving'}</p>
              <Link to="/giving" className="btn-primary" data-testid="donate-btn">{settings.quicklink3_button || 'Donate Now'}</Link>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        imageUrl={lightboxImage.url}
        imageAlt={lightboxImage.alt}
      />
    </div>
  );
}

export default Home;